/**
 * MaskedCoordinateInput - Coordinate input with privacy masking
 * Supports both decimal and DMS formats with segmented inputs
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
  HStack,
  VStack,
  Input,
  Text,
  Select
} from '@chakra-ui/react';
import { VisibilityToggle } from '../../common/VisibilityToggle';
import {
  CoordinateFormat,
  DMSCoordinate
} from '../../../types/PrivacyTypes';
import {
  formatDecimalCoordinate,
  formatDMS,
  decimalToDMS,
  dmsToDecimal,
  parseCoordinatePairString
} from '../../../utils/privacyUtils';

interface MaskedCoordinateInputProps {
  latitude: number;
  longitude: number;
  onChange: (lat: number, lng: number) => void;
  format: CoordinateFormat;
  onFormatChange: (format: CoordinateFormat) => void;
  masked: boolean;
  onMaskedChange: (masked: boolean) => void;
  disabled?: boolean;
  size?: 'sm' | 'md';
}

export const MaskedCoordinateInput: React.FC<MaskedCoordinateInputProps> = ({
  latitude,
  longitude,
  onChange,
  format,
  onFormatChange,
  masked,
  onMaskedChange,
  disabled = false,
  size = 'sm'
}) => {
  // Convert to DMS for display/editing
  const latDMS = useMemo(() => decimalToDMS(latitude, 'lat'), [latitude]);
  const lonDMS = useMemo(() => decimalToDMS(longitude, 'lon'), [longitude]);

  // Local state for DMS segment editing
  const [latDeg, setLatDeg] = useState('');
  const [latMin, setLatMin] = useState('');
  const [latSec, setLatSec] = useState('');
  const [latDir, setLatDir] = useState<'N' | 'S'>('N');

  const [lonDeg, setLonDeg] = useState('');
  const [lonMin, setLonMin] = useState('');
  const [lonSec, setLonSec] = useState('');
  const [lonDir, setLonDir] = useState<'E' | 'W'>('E');

  // Sync local state with props when not editing
  const [isEditingLat, setIsEditingLat] = useState(false);
  const [isEditingLon, setIsEditingLon] = useState(false);

  // For decimal mode
  const [decimalLatInput, setDecimalLatInput] = useState('');
  const [decimalLonInput, setDecimalLonInput] = useState('');
  const [editingDecimalLat, setEditingDecimalLat] = useState(false);
  const [editingDecimalLon, setEditingDecimalLon] = useState(false);

  // Format displayed values for decimal mode
  const displayLatitude = useMemo(() => {
    return formatDecimalCoordinate(latitude, masked);
  }, [latitude, masked]);

  const displayLongitude = useMemo(() => {
    return formatDecimalCoordinate(longitude, masked);
  }, [longitude, masked]);

  // Handle numeric-only input for DMS fields
  const handleNumericChange = (
    value: string,
    maxLength: number,
    maxValue: number,
    setter: (v: string) => void
  ) => {
    const digitsOnly = value.replace(/\D/g, '').slice(0, maxLength);
    const num = parseInt(digitsOnly, 10);
    if (digitsOnly === '' || (num >= 0 && num <= maxValue)) {
      setter(digitsOnly);
    }
  };

  // Start editing latitude DMS
  const startEditingLatDMS = useCallback(() => {
    if (masked) return;
    setLatDeg(latDMS.degrees.toString());
    setLatMin(latDMS.minutes.toString().padStart(2, '0'));
    setLatSec(latDMS.seconds.toString().padStart(2, '0'));
    setLatDir(latDMS.direction as 'N' | 'S');
    setIsEditingLat(true);
  }, [masked, latDMS]);

  // Start editing longitude DMS
  const startEditingLonDMS = useCallback(() => {
    if (masked) return;
    setLonDeg(lonDMS.degrees.toString());
    setLonMin(lonDMS.minutes.toString().padStart(2, '0'));
    setLonSec(lonDMS.seconds.toString().padStart(2, '0'));
    setLonDir(lonDMS.direction as 'E' | 'W');
    setIsEditingLon(true);
  }, [masked, lonDMS]);

  // Apply latitude DMS changes
  const applyLatDMS = useCallback(() => {
    const deg = parseInt(latDeg, 10) || 0;
    const min = parseInt(latMin, 10) || 0;
    const sec = parseInt(latSec, 10) || 0;

    if (deg <= 90 && min < 60 && sec < 60) {
      const dms: DMSCoordinate = {
        degrees: deg,
        minutes: min,
        seconds: sec,
        direction: latDir
      };
      const decimal = dmsToDecimal(dms);
      if (Math.abs(decimal) <= 90) {
        onChange(decimal, longitude);
      }
    }
    setIsEditingLat(false);
  }, [latDeg, latMin, latSec, latDir, longitude, onChange]);

  // Apply longitude DMS changes
  const applyLonDMS = useCallback(() => {
    const deg = parseInt(lonDeg, 10) || 0;
    const min = parseInt(lonMin, 10) || 0;
    const sec = parseInt(lonSec, 10) || 0;

    if (deg <= 180 && min < 60 && sec < 60) {
      const dms: DMSCoordinate = {
        degrees: deg,
        minutes: min,
        seconds: sec,
        direction: lonDir
      };
      const decimal = dmsToDecimal(dms);
      if (Math.abs(decimal) <= 180) {
        onChange(latitude, decimal);
      }
    }
    setIsEditingLon(false);
  }, [lonDeg, lonMin, lonSec, lonDir, latitude, onChange]);

  // Handle decimal input
  const handleDecimalLatChange = useCallback((value: string) => {
    const pair = parseCoordinatePairString(value);
    if (pair) {
      onChange(pair.lat, pair.lng);
      return;
    }
    const parsed = parseFloat(value);
    if (!isNaN(parsed) && Math.abs(parsed) <= 90) {
      onChange(parsed, longitude);
    }
  }, [longitude, onChange]);

  const handleDecimalLonChange = useCallback((value: string) => {
    const pair = parseCoordinatePairString(value);
    if (pair) {
      onChange(pair.lat, pair.lng);
      return;
    }
    const parsed = parseFloat(value);
    if (!isNaN(parsed) && Math.abs(parsed) <= 180) {
      onChange(latitude, parsed);
    }
  }, [latitude, onChange]);

  // Common input styles matching time fields
  const segmentInputProps = {
    size: size,
    px: '4px',
    textAlign: 'center' as const,
    inputMode: 'numeric' as const,
    bg: masked ? 'gray.50' : 'white',
    isDisabled: disabled
  };

  return (
    <VStack spacing={2} w="full" align="stretch">
      {/* Format selector and visibility toggle */}
      <HStack justify="space-between" w="full">
        <Select
          size="xs"
          w="90px"
          value={format}
          onChange={(e) => onFormatChange(e.target.value as CoordinateFormat)}
          isDisabled={disabled}
        >
          <option value="decimal">Decimal</option>
          <option value="dms">DMS</option>
        </Select>
        <VisibilityToggle
          isVisible={!masked}
          onToggle={() => onMaskedChange(!masked)}
          size="xs"
          ariaLabel={masked ? 'Show coordinates' : 'Hide coordinates'}
        />
      </HStack>

      {format === 'dms' ? (
        /* DMS Mode - Segmented inputs */
        <>
          {/* Latitude DMS */}
          <HStack spacing={1} w="full" justify="center">
            <Text fontSize="xs" color="gray.500" w="24px" flexShrink={0}>Lat</Text>
            <Input
              {...segmentInputProps}
              w="42px"
              maxLength={2}
              placeholder="DD"
              value={isEditingLat ? latDeg : latDMS.degrees.toString()}
              onChange={(e) => handleNumericChange(e.target.value, 2, 90, setLatDeg)}
              onFocus={() => startEditingLatDMS()}
              onBlur={applyLatDMS}
              isReadOnly={masked}
            />
            <Text color="gray.500" fontSize="sm">°</Text>
            <Input
              {...segmentInputProps}
              w="42px"
              maxLength={2}
              placeholder="MM"
              value={isEditingLat ? latMin : (masked ? `${latDMS.minutes.toString().padStart(2, '0').charAt(0)}•` : latDMS.minutes.toString().padStart(2, '0'))}
              onChange={(e) => handleNumericChange(e.target.value, 2, 59, setLatMin)}
              onFocus={() => { if (!isEditingLat) startEditingLatDMS(); }}
              onBlur={applyLatDMS}
              isReadOnly={masked}
            />
            <Text color="gray.500" fontSize="sm">'</Text>
            <Input
              {...segmentInputProps}
              w="42px"
              maxLength={2}
              placeholder="SS"
              value={isEditingLat ? latSec : (masked ? '••' : latDMS.seconds.toString().padStart(2, '0'))}
              onChange={(e) => handleNumericChange(e.target.value, 2, 59, setLatSec)}
              onFocus={() => { if (!isEditingLat) startEditingLatDMS(); }}
              onBlur={applyLatDMS}
              isReadOnly={masked}
            />
            <Text color="gray.500" fontSize="sm">"</Text>
            <Select
              size="xs"
              w="62px"
              value={isEditingLat ? latDir : latDMS.direction}
              onChange={(e) => {
                if (!isEditingLat) startEditingLatDMS();
                setLatDir(e.target.value as 'N' | 'S');
              }}
              onBlur={applyLatDMS}
              isDisabled={disabled || masked}
            >
              <option value="N">N</option>
              <option value="S">S</option>
            </Select>
          </HStack>

          {/* Longitude DMS */}
          <HStack spacing={1} w="full" justify="center">
            <Text fontSize="xs" color="gray.500" w="24px" flexShrink={0}>Lon</Text>
            <Input
              {...segmentInputProps}
              w="42px"
              maxLength={3}
              placeholder="DDD"
              value={isEditingLon ? lonDeg : lonDMS.degrees.toString()}
              onChange={(e) => handleNumericChange(e.target.value, 3, 180, setLonDeg)}
              onFocus={() => startEditingLonDMS()}
              onBlur={applyLonDMS}
              isReadOnly={masked}
            />
            <Text color="gray.500" fontSize="sm">°</Text>
            <Input
              {...segmentInputProps}
              w="42px"
              maxLength={2}
              placeholder="MM"
              value={isEditingLon ? lonMin : (masked ? `${lonDMS.minutes.toString().padStart(2, '0').charAt(0)}•` : lonDMS.minutes.toString().padStart(2, '0'))}
              onChange={(e) => handleNumericChange(e.target.value, 2, 59, setLonMin)}
              onFocus={() => { if (!isEditingLon) startEditingLonDMS(); }}
              onBlur={applyLonDMS}
              isReadOnly={masked}
            />
            <Text color="gray.500" fontSize="sm">'</Text>
            <Input
              {...segmentInputProps}
              w="42px"
              maxLength={2}
              placeholder="SS"
              value={isEditingLon ? lonSec : (masked ? '••' : lonDMS.seconds.toString().padStart(2, '0'))}
              onChange={(e) => handleNumericChange(e.target.value, 2, 59, setLonSec)}
              onFocus={() => { if (!isEditingLon) startEditingLonDMS(); }}
              onBlur={applyLonDMS}
              isReadOnly={masked}
            />
            <Text color="gray.500" fontSize="sm">"</Text>
            <Select
              size="xs"
              w="62px"
              value={isEditingLon ? lonDir : lonDMS.direction}
              onChange={(e) => {
                if (!isEditingLon) startEditingLonDMS();
                setLonDir(e.target.value as 'E' | 'W');
              }}
              onBlur={applyLonDMS}
              isDisabled={disabled || masked}
            >
              <option value="E">E</option>
              <option value="W">W</option>
            </Select>
          </HStack>
        </>
      ) : (
        /* Decimal Mode - Single inputs per coordinate */
        <>
          <HStack spacing={2} w="full">
            <Text fontSize="xs" color="gray.500" w="24px" flexShrink={0}>Lat</Text>
            <Input
              placeholder="Latitude (or paste coordinates)"
              value={editingDecimalLat ? decimalLatInput : displayLatitude}
              onChange={(e) => setDecimalLatInput(e.target.value)}
              onFocus={() => {
                if (!masked) {
                  setDecimalLatInput(latitude.toFixed(6));
                  setEditingDecimalLat(true);
                }
              }}
              onBlur={() => {
                handleDecimalLatChange(decimalLatInput);
                setEditingDecimalLat(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleDecimalLatChange(decimalLatInput);
                  setEditingDecimalLat(false);
                }
              }}
              size={size}
              w="full"
              textAlign="center"
              isReadOnly={masked}
              bg={masked ? 'gray.50' : 'white'}
              isDisabled={disabled}
            />
          </HStack>

          <HStack spacing={2} w="full">
            <Text fontSize="xs" color="gray.500" w="24px" flexShrink={0}>Lon</Text>
            <Input
              placeholder="Longitude (or paste coordinates)"
              value={editingDecimalLon ? decimalLonInput : displayLongitude}
              onChange={(e) => setDecimalLonInput(e.target.value)}
              onFocus={() => {
                if (!masked) {
                  setDecimalLonInput(longitude.toFixed(6));
                  setEditingDecimalLon(true);
                }
              }}
              onBlur={() => {
                handleDecimalLonChange(decimalLonInput);
                setEditingDecimalLon(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleDecimalLonChange(decimalLonInput);
                  setEditingDecimalLon(false);
                }
              }}
              size={size}
              w="full"
              textAlign="center"
              isReadOnly={masked}
              bg={masked ? 'gray.50' : 'white'}
              isDisabled={disabled}
            />
          </HStack>
        </>
      )}
    </VStack>
  );
};

export default MaskedCoordinateInput;
